import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Divider,
    LinearProgress,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import EmptyState from './EmptyState';

/**
 * Desktop styled DataGrid (the original).
 */
const DesktopGrid = styled(DataGrid)(({ theme }) => ({
    border: 0,
    color: theme.palette.mode === 'light' ? 'rgba(0,0,0,.85)' : 'rgba(255,255,255,0.85)',
    fontSize: '0.85rem',
    WebkitFontSmoothing: 'auto',
    letterSpacing: 'normal',
    '& .MuiDataGrid-columnsContainer': {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1d1d1d'
    },
    '& .MuiDataGrid-iconSeparator': { display: 'none' },
    '& .MuiDataGrid-cell': {
        color: theme.palette.mode === 'light' ? 'rgba(0,0,0,.85)' : 'rgba(255,255,255,0.65)'
    },
    '& .MuiPaginationItem-root': { borderRadius: 0 },
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': { outline: 'none !important' }
}));

// ─────────────────────────────────────────────────────────────────────────────
// Mobile card-list view, driven by the SAME `columns` array as DataGrid.
// Each row becomes a Card. Each column becomes a label + rendered cell pair.
//
// Column conventions (opt-in via column extras, but works without them):
//   - mobileHide: true        → column not shown on mobile
//   - mobileSlot: 'header'    → rendered prominently at the top of the card (status chips, titles)
//   - mobileSlot: 'action'    → rendered at top-right of the card (action buttons / menu)
//   - mobileLabel: 'Custom'   → override headerName as the label text
//   - Otherwise: heuristic by field name → 'status'/'Status' goes to header,
//     'Action'/'action'/'actions' goes to action slot.
// ─────────────────────────────────────────────────────────────────────────────

const classifyColumn = (col) => {
    if (col.mobileSlot) return col.mobileSlot;
    const f = String(col.field || '').toLowerCase();
    if (f === 'status') return 'header';
    if (f === 'action' || f === 'actions') return 'action';
    return 'body';
};

const renderColumnCell = (col, row, getRowId) => {
    const value = row?.[col.field];
    const id = getRowId ? getRowId(row) : row?.id;
    if (typeof col.renderCell === 'function') {
        // Build a params object compatible with the DataGrid renderCell contract.
        return col.renderCell({
            id,
            field: col.field,
            value,
            row,
            api: undefined,
            getValue: (rowId, field) => row?.[field],
            colDef: col,
            cellMode: 'view',
            hasFocus: false,
            tabIndex: -1
        });
    }
    if (typeof col.valueFormatter === 'function') {
        return col.valueFormatter({ value, field: col.field, id, api: undefined });
    }
    if (typeof col.valueGetter === 'function') {
        return col.valueGetter({ row, value, field: col.field, id });
    }
    if (value === undefined || value === null || value === '') return '-';
    return String(value);
};

const MobileCardList = ({ rows, columns, getRowId, loading }) => {
    const visibleColumns = useMemo(() => columns.filter((c) => !c.mobileHide && !c.hide), [columns]);
    const headerCols = visibleColumns.filter((c) => classifyColumn(c) === 'header');
    const actionCols = visibleColumns.filter((c) => classifyColumn(c) === 'action');
    const bodyCols = visibleColumns.filter((c) => classifyColumn(c) === 'body');

    // Rows arrive already paginated from the parent (server-side pagination is the project pattern).
    // We render exactly what we receive — the parent's <Pagination> at the bottom of the page handles paging.
    const pageRows = rows || [];

    if (loading) {
        return (
            <Stack spacing={1.5} sx={{ p: 1 }}>
                <LinearProgress />
                {[0, 1, 2].map((i) => (
                    <Card key={i} sx={{ opacity: 0.5 }}>
                        <CardContent>
                            <Box sx={{ height: 80 }} />
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (!rows || rows.length === 0) {
        return <EmptyState message="No data" description="Adjust filters or pull data again" />;
    }

    return (
        <Stack spacing={1.5} sx={{ p: 1, width: '100%', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
            {pageRows.map((row) => {
                const id = getRowId ? getRowId(row) : row?.id;
                return (
                    <Card key={id} sx={{ width: '100%', flexShrink: 0 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            {/* Header strip: header columns left, action columns right */}
                            {(headerCols.length > 0 || actionCols.length > 0) && (
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: bodyCols.length ? 1 : 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                                        {headerCols.map((col) => (
                                            <Box key={col.field}>{renderColumnCell(col, row, getRowId)}</Box>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        {actionCols.map((col) => (
                                            <Box key={col.field}>{renderColumnCell(col, row, getRowId)}</Box>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}

                            {bodyCols.length > 0 && headerCols.length + actionCols.length > 0 && (
                                <Divider sx={{ mb: 1 }} />
                            )}

                            {/* Body rows: label on top, value below — full-width on mobile */}
                            <Stack spacing={1}>
                                {bodyCols.map((col) => (
                                    <Box key={col.field} sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.68rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                display: 'block',
                                                mb: 0.25
                                            }}
                                            noWrap
                                        >
                                            {col.mobileLabel || col.headerName || col.field}
                                        </Typography>
                                        <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                            {renderColumnCell(col, row, getRowId)}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}

        </Stack>
    );
};

/**
 * StyledDataGrid — drop-in DataGrid with a built-in mobile card view.
 *
 * Opt out: pass `mobile={false}` to always use DataGrid (e.g. heavy spreadsheets).
 */
const StyledDataGrid = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { rows, columns, getRowId, loading, mobile = true, ...rest } = props;

    if (isMobile && mobile && Array.isArray(columns)) {
        return (
            <MobileCardList
                rows={rows || []}
                columns={columns}
                getRowId={getRowId}
                loading={loading}
            />
        );
    }

    const mergedComponents = {
        NoRowsOverlay: () => <EmptyState message="No data" description="Adjust filters or pull data again" />,
        NoResultsOverlay: () => <EmptyState message="No results" description="Try a different search" />,
        ...(rest.components || {})
    };

    return (
        <DesktopGrid
            rows={rows}
            columns={columns}
            getRowId={getRowId}
            loading={loading}
            rowsPerPageOptions={rest.rowsPerPageOptions || [10, 20, 50, 100]}
            {...rest}
            components={mergedComponents}
        />
    );
};

export default StyledDataGrid;
